import { Injectable } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { guid } from '@datorama/akita';
import { AkitaNgFormsManager } from '@datorama/akita-ng-forms-manager';
import {
	ComponentFormController,
	filterNil,
} from '@lycoris-nubila/ngx-lycoris';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import { Observable } from 'rxjs';
import {
	distinctUntilChanged,
	filter,
	map,
	pluck,
	shareReplay,
	switchMap,
	tap,
} from 'rxjs/operators';

import {
	SetModuleRatingDialogState,
	SetModuleRatingDialogStore,
} from 'dialog/set-module-rating-dialog/set-module-rating-dialog.store';
import { ModuleApplicationPort } from 'module/port/module-application.port';
import { UserApplicationPort } from 'user/port/user-application.port';
import { IUserModuleSuggestion } from 'user/suggestion/user-module-suggestion';

@Injectable()
@UntilDestroy()
export class SetModuleRatingDialogController extends ComponentFormController<
	SetModuleRatingDialogState,
	any
> {
	public baseI18n = 'presentation.app.common.dialog.userModuleRating.';

	public moduleSuggestions$: Observable<IUserModuleSuggestion[]>;
	public moduleName$: Observable<string>;
	public sessionUserId$: Observable<string>;

	constructor(
		fb: FormBuilder,
		formsManager: AkitaNgFormsManager,
		store: SetModuleRatingDialogStore,
		private _translateService: TranslateService,
		private _userApplicationPort: UserApplicationPort,
		private _moduleApplicationPort: ModuleApplicationPort
	) {
		super(
			store,
			formsManager,
			`set-module-rating-${guid()}`,
			fb.group({
				rating: ['', Validators.required],
			}),
			{ debounceTime: 250 }
		);
		this._initObservables();
		this._initListeners();
	}

	private _initObservables() {
		this.sessionUserId$ = this._userApplicationPort
			.observeSessionUserId()
			.pipe(filterNil(), shareReplay({ bufferSize: 1, refCount: true }));

		this.moduleSuggestions$ = this._userApplicationPort
			.observeUserModuleSuggestions()
			.pipe(shareReplay({ bufferSize: 1, refCount: true }));

		this.moduleName$ = this.select('moduleId').pipe(
			filterNil(),
			switchMap((moduleId) =>
				this._moduleApplicationPort.observeModuleById(moduleId)
			),
			map((data) => data.data),
			filterNil(),
			switchMap((module) => module.localization$),
			pluck('name'),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	private _initListeners() {
		this._userApplicationPort
			.fetchUserModuleSuggestions()
			.pipe(untilDestroyed(this))
			.subscribe();

		this.form.valueChanges
			.pipe(
				distinctUntilChanged(isEqual),
				tap(() => this.saveRating()),
				untilDestroyed(this)
			)
			.subscribe();

		this.select(['moduleId', 'coacheeId'])
			.pipe(
				filter((state) => !!state.coacheeId && !!state.moduleId),
				switchMap((state) =>
					this._userApplicationPort.observeUserModules(state.coacheeId).pipe(
						map((data) => data.data),
						map((userModules) =>
							(userModules || []).find((um) => um.moduleId === state.moduleId)
						)
					)
				),
				filterNil(),
				tap((userModule) =>
					this.form.patchValue({
						rating: userModule.moduleRatingValue || 0,
					})
				),
				untilDestroyed(this)
			)
			.subscribe();
	}

	public saveRating(): Promise<void> {
		return this._userApplicationPort.updateUserModuleRating({
			moduleRatingValue: this.form.value.rating,
			moduleId: this.getValue().moduleId,
			userId: this.getValue().coacheeId,
			moduleRatingComment: null,
		});
	}

	public setData(coacheeId: string, moduleId: string) {
		this.store.update({ coacheeId, moduleId });
	}
}
